import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  Container,
  Divider,
  Grid,
  Tab,
  Tabs,
  TextField,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress
} from "@mui/material";
import { Check } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import ParentLoginDialog from "../../pages/ParentLogin";
import { createOrder, verifyPayment, createSubscription, verifySubscription } from "../../api/parentApi";
import { scheduleDowngradePlan } from "../../api/parentApi";
import { Snackbar, Alert } from "@mui/material";

/* =========================
   SCHOOL PLANS (YEARLY BASE)
========================= */

const schoolPlans = [
  {
    id: "free",
    title: "Free",
    yearlyPrice: 0,
    features: [
      "School Management Dashboard",
      "Parent Mobile App Access",
      "Unlimited Parents Access",
      "Student Profiles",
      "Attendance Management",
      "Homework & Assignments",
      "School Announcements"
    ],
  },
  {
    id: "growth",
    title: "Growth",
    yearlyPrice: 149,
    badge: "⭐ Most schools choose this",
    features: [
      "All Free Features",
      "Parent Communication",
      "Live Transport Monitoring",
      "Priority Support"
    ],
  },
  {
    id: "premium",
    title: "Premium",
    yearlyPrice: 599,
    features: [
      "All Free Features",
      "Unlimited AI Homework Assistant for Students",
      "Advanced Reports & Analytics",
      "Priority Support"
    ],
  }
];

const customPlan = {
  title: "Custom Plan",
  features: [
    "For Schools with 3000+ Students",
    "Multi-Campus Support",
    "Optional Feature Add-Ons",
    "Higher AI Usage Limits",
    "Dedicated Onboarding Assistance",
  ],
};

/* =========================
   PARENT PLANS
========================= */
const parentPlans = [
  {
    id: "free",
    title: "Free",
    subtitle: "1 student • Essential school updates",
    variants: [
      {
        id: "free",
        "subscriptionId": "free",
        "subscriptionUrl": "",
        label: "1",
        price: 0,
        maxStudents: 1
      }
    ],
    type: "individual",
    cta: "Download the App",
    features: [
      "View Academic Updates",
      "School Announcements",
      "Student Profile Overview",
      "Homework Viewing & Submission",
      "Parent–Teacher Communication",
      "Exam Results",
      "Fee Records",
      "Basic Transport Updates"
    ],
  },
  {
    id: "foundation",
    title: "Foundation",
    subtitle: "For 1–5 students • Pay based on number of children",
    variants: [
      { id: "plan_SZ283EiSNb4iWq", "subscriptionId": "sub_SZ2Q8gJSeDwDyy", "subscriptionUrl": "https://rzp.io/rzp/XykU1mpN", label: "1", price: 29, maxStudents: 1 },
      { id: "plan_SZ26tX1GBmdrIa", "subscriptionId": "sub_SZ2Tyr8fdq40uS", "subscriptionUrl": "https://rzp.io/rzp/hNwEad2F", label: "3", price: 79, maxStudents: 3 },
      { id: "plan_SZ28WEnPott2xC", "subscriptionId": "sub_SZ2TInAHRTPVye", "subscriptionUrl": "https://rzp.io/rzp/jOST56h", label: "5", price: 109, maxStudents: 5 },
    ],
    type: "individual",
    cta: "Choose Plan",
    features: [
      "All Free Features",
      "Instant Homework Help",
      "Step-by-step Solutions",
      "Practice Questions",
      "Homework Reminders",
      "Advanced Transport Tracking"
    ],
  },
  {
    id: "core_family",
    title: "Core Family",
    subtitle: "2 students • Better performance for your child",
    variants: [
      {
        id: "plan_SZ2EA4sCaBbNZH",
        "subscriptionId": "sub_SZ2SPmWmpzFyPQ",
        "subscriptionUrl": "https://rzp.io/rzp/kWWLPmZ",
        label: "2",
        price: 99,
        maxStudents: 2
      }
    ],
    type: "family",
    badge: "MOST_POPULAR",
    cta: "Choose Plan",
    features: [
      "All Foundation Features",
      "Faster Doubt Solving",
      "Practice & Revision Questions",
      "100 Questions / month",
      "Basic Performance Insights"
    ],
  },
  {
    id: "plus_family",
    title: "Plus Family",
    subtitle: "5 students • Complete academic support at home",
    variants: [
      {
        id: "plan_SZ2Elk6VDasirI",
        "subscriptionId": "sub_SZ2RfkX44VdUnK",
        "subscriptionUrl": "https://rzp.io/rzp/kCCyAfh0",
        label: "5",
        price: 249,
        maxStudents: 5
      }
    ],
    type: "family",
    cta: "Choose Plan",
    features: [
      "All Core Features",
      "Unlimited Doubt Solving",
      "Advanced Performance Insights",
      "Weak Topic Detection",
      "Personalized Study Suggestions",
      "Exam Preparation Support"
    ],
  },
];

const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

/* =========================
   COMPONENT
========================= */

const PricingPlan = () => {
  const [selectedVariant, setSelectedVariant] = useState({});
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [billing, setBilling] = useState("yearly");
  const [students, setStudents] = useState(500);
  const [openLogin, setOpenLogin] = useState(false);
  const hidePlanTab = true;

  const getDiscount = (count) => {
    if (count >= 2000) return 0.15;
    if (count >= 1000) return 0.1;
    return 0;
  };

  const storedToken = localStorage.getItem("parentToken");
  const storedParent = localStorage.getItem("parentInfo");
  const isLoggedIn = !!storedToken && !!storedParent;
  const parentDetails = storedParent ? JSON.parse(storedParent) : null;
  // Assume a default plan price of 0 (Free) if not specified in parent profile
  const currentPlanPrice = parentDetails?.planPrice || 0;
  const [parentData, setParentData] = useState(parentDetails);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async (plan, variant) => {
    const res = await loadRazorpayScript();

    if (!res) {
      alert("Payment gateway failed to loyad. Are you online?");
      return;
    }

    setLoading(true);
    try {
      const res = await createSubscription(
        {
          parent_plan_id: plan.id,
          plan_id: variant.id,
          total_count: 12 // Total number of billing cycles
        },
        storedToken
      );

      const data = res.response;

      const options = {
        key: "rzp_test_SRTmdF0BA7f6o2",
        subscription_id: data.subscription_id,
        name: "MySchoolling - A product of Versynix Global Pvt. Ltd.",
        description: plan.subtitle,

        handler: async function (response) {
          try {
            const verify = await verifySubscription(storedToken);

            if (verify.meta?.success) {
              const updateparent = {
                ...parentData,
                planPrice: variant.price,
                planName: plan.title
              };
              setParentData(updateparent);
              localStorage.setItem("parentInfo", JSON.stringify(updateparent));

              alert(`Plan purchased successfully! Payment ID: ${response.razorpay_payment_id}`);
            } else {
              alert("Failed to purchase/renew plan! Please try again.");
            }
          } catch (error) {
            console.error("Payment verification failed:", error);
            alert("Failed to purchase/renew plan! Please try again.");
          }
        },

        prefill: {
          name: parentData?.name || "",
          email: parentData?.email || "",
          contact: parentData?.mobileNo || "",
        },

        notes: {
          address: "MySchoolling Office",
        },

        theme: {
          color: "#152259",
        },

        modal: {
          ondismiss: function () {
            document.body.style.overflow = "";
            document.documentElement.style.overflow = "";
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();

      // 🔥 redirect to Razorpay hosted page
      // window.location.href = data.checkout_url;

    } catch (err) {
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async (plan, variant) => {
    const res = await loadRazorpayScript();

    if (!res) {
      alert("Razorpay SDK failed to load. Are you online?");
      return;
    }

    try {
      // 🔹 Step 1: Create order
      const res = await createOrder(
        { amount: variant.price * 100 },
        storedToken
      );

      const order = res.response.order;

      // 🔹 Step 2: Razorpay options
      const options = {
        key: "rzp_test_SRTmdF0BA7f6o2",
        amount: order.amount,
        currency: "INR",
        name: "MySchoolling - A product of Versynix Global Pvt. Ltd.",
        description: `Upgrade to ${plan.title}`,
        image: "/logoWhite.png",
        order_id: order.id,

        handler: async function (response) {
          // alert(`Payment successful! Payment ID: ${response.razorpay_payment_id}`);

          try {
            const verify = await verifyPayment(
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                amount: variant.price,
                plan_name: plan.title,
                plan_key: variant.id,
                max_students: variant.maxStudents
              },
              storedToken
            );

            if (verify.meta?.success) {
              const updateparent = {
                ...parentData,
                planPrice: variant.price,
                planName: plan.title
              };
              setParentData(updateparent);
              localStorage.setItem("parentInfo", JSON.stringify(updateparent));

              alert(`Plan purchased successfully! Payment ID: ${response.razorpay_payment_id}`);
            } else {
              alert("Failed to purchase/renew plan! Please try again.");
            }
          } catch (error) {
            console.error("Payment verification failed:", error);
            alert("Failed to purchase/renew plan! Please try again.");
          }
        },

        prefill: {
          name: parentData?.name || "",
          email: parentData?.email || "",
          contact: parentData?.mobileNo || "",
        },

        notes: {
          address: "",
        },

        theme: {
          color: "#152259",
        },

        modal: {
          ondismiss: function () {
            document.body.style.overflow = "";
            document.documentElement.style.overflow = "";
          },
        },
      };

      const rzp1 = new window.Razorpay(options);

      rzp1.on("payment.failed", function (response) {
        document.body.style.overflow = "";
        document.documentElement.style.overflow = "";
        alert(`Payment failed: ${response.error.description}`);
      });

      rzp1.open();

    } catch (error) {
      console.error("Create order failed:", error);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ pt: 14, pb: 14 }}>
      {/* HEADER */}
      <Box textAlign="center" mb={4}>
        <Typography variant="h2" fontWeight={700}>
          Flexible Pricing for Every Need
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Transparent. Scalable. Powerful.
        </Typography>
      </Box>

      {/* TABS */}
      {hidePlanTab || !localStorage.getItem("parentToken") && (
        <Box display="flex" justifyContent="center" mb={6}>
          <Tabs value={tab} onChange={(e, v) => setTab(v)}>
            <Tab label="For Parents" />
            <Tab label="For Schools" />
          </Tabs>
        </Box>
      )}

      {/* ================= PARENTS ================= */}
      {tab === 0 && (
        <Grid container spacing={4} justifyContent="center">
          {parentPlans.map((plan) => {
            const variant = selectedVariant[plan.id] || plan.variants[0];
            let buttonText = plan.cta;
            ; let buttonAction = () => setOpenLogin(true);
            let buttonDisabled = false;

            let isCurrentPlan = false;

            if (isLoggedIn) {
              if (variant.price === currentPlanPrice) {
                buttonText = "Current Plan";
                buttonDisabled = true;
                isCurrentPlan = true;
              } else if (variant.price > currentPlanPrice) {
                buttonText = "Upgrade";
                buttonAction = () => handleSubscribe(plan, variant);
              } else {
                buttonText = "Downgrade";
                buttonAction = async () => {
                  const response = await scheduleDowngradePlan({
                    plan_key: variant.id,
                    plan_name: plan.title
                  }, storedToken);

                  if (response.meta?.success) {
                    const expiryDate = new Date(response.response.expiry_date);
                    const formattedDate = expiryDate.toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    });

                    // setSnackbar({
                    //   open: true,
                    //   message: `${response.response.plan_name} Plan (Current),\nDowngrade scheduled on ${formattedDate} to ${response.response.next_plan_name}`,
                    //   severity: "success",
                    // });
                    alert(
                      `${response.response.plan_name} Plan (Current),\nDowngrade scheduled on ${formattedDate} to ${response.response.next_plan_name}`
                    );
                  }
                };
              }
            }

            return (
              <Grid item xs={12} sm={8} md={3} key={plan.id}>
                <Card
                  sx={{
                    p: 4,
                    borderRadius: 4,
                    border: isCurrentPlan ? "2px solid #2e7d32" : "none",
                    background: isCurrentPlan ? "#f1f8f4" : "#fff",
                  }}
                >
                  {plan.badge && (
                    <Chip
                      label="⭐ Most parents choose this"
                      color="primary"
                      sx={{ mb: 2 }}
                    />
                  )}

                  <Typography variant="h5" fontWeight={700}>
                    {plan.title}
                  </Typography>

                  {plan.id === "foundation" && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      mt={1}
                      fontWeight={500}
                    >
                      Select number of students
                    </Typography>
                  )}

                  <Typography variant="h4" mt={0} fontWeight={700}>
                    {plan.variants.length > 1 && (
                      <Box sx={{ mt: 1, mr: 0, mb: 1 }}>
                        {plan.variants.map((v) => (
                          <Button
                            key={v.id}
                            size="regular"
                            variant={variant.id === v.id ? "contained" : "outlined"}
                            sx={{
                              mr: 1,
                              mb: 1,
                              px: 2,
                              py: 1,
                              fontSize: "13px",
                              minWidth: "auto",

                            }}
                            onClick={() =>
                              setSelectedVariant({
                                ...selectedVariant,
                                [plan.id]: v,
                              })
                            }
                          >
                            {v.label}
                          </Button>
                        ))}
                      </Box>
                    )}
                    ₹{variant.price}
                    <Typography component="span" variant="body1">
                      {" "}
                      / month
                    </Typography>
                  </Typography>

                  <Typography variant="body2" mt={1} color="text.secondary">
                    {plan.subtitle}
                  </Typography>

                  <Divider sx={{ my: 3 }} />

                  {plan.features.map((feature, i) => (
                    <Box key={i} display="flex" mb={1.5}>
                      <Check sx={{ mr: 1, color: isCurrentPlan ? "#2e7d32" : "inherit" }} />
                      <Typography variant="body2">{feature}</Typography>
                    </Box>
                  ))}

                  <Button
                    fullWidth
                    variant={isCurrentPlan ? "outlined" : "contained"}
                    color={isCurrentPlan ? "success" : "primary"}
                    disabled={buttonDisabled || loading}
                    sx={{ mt: 3 }}
                    onClick={buttonAction}
                  >
                    {loading ? (
                      <CircularProgress size={22} color="inherit" />
                    ) : (
                      buttonText
                    )}
                  </Button>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )
      }


      {/* ================= SCHOOLS ================= */}
      {
        tab === 1 && (
          <>
            {/* Billing Toggle */}
            <Box display="flex" justifyContent="center" mb={6}>
              <ToggleButtonGroup
                value={billing}
                exclusive
                onChange={(e, val) => val && setBilling(val)}
              >
                <ToggleButton value="yearly">Yearly</ToggleButton>
                <ToggleButton value="quarterly">Quarterly</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Student Input */}
            <Box textAlign="center" mb={6}>
              <TextField
                type="number"
                label="Number of Students"
                value={students}
                onChange={(e) => setStudents(Number(e.target.value))}
                sx={{ maxWidth: 250 }}
              />
              <Typography variant="caption" display="block" mt={1}>
                10% off for 1000+ students | 15% off for 2000+ students
              </Typography>
            </Box>

            <Grid container spacing={5} justifyContent="center">
              {schoolPlans.map((plan) => {
                const discount = getDiscount(students);
                const yearlyAfterDiscount =
                  plan.yearlyPrice * (1 - discount);

                const pricePerPeriod =
                  billing === "yearly"
                    ? yearlyAfterDiscount
                    : yearlyAfterDiscount / 4;

                const total =
                  billing === "yearly"
                    ? students * yearlyAfterDiscount
                    : students * (yearlyAfterDiscount / 4);

                const monthlyEquivalent =
                  (yearlyAfterDiscount / 12).toFixed(2);

                return (
                  <Grid item xs={12} sm={6} md={4} key={plan.id}>
                    <Card sx={{ p: 4, borderRadius: 4, height: "100%" }}>
                      {plan.badge && (
                        <Chip
                          label="⭐ Most schools choose this"
                          color="primary"
                          sx={{ mb: 2 }}
                        />
                      )}

                      <Typography variant="h5" fontWeight={700}>
                        {plan.title}
                      </Typography>

                      <Typography variant="h4" mt={2} fontWeight={700}>
                        ₹{pricePerPeriod.toFixed(0)}
                        <Typography component="span" variant="body2">
                          {" "}
                          / student /{" "}
                          {billing === "year" ? "year" : billing === "quarterly" ? "quarter" : "year"}
                        </Typography>
                      </Typography>

                      <Typography variant="body2" mt={1} color="text.secondary">
                        ₹{monthlyEquivalent} / student / month equivalent
                      </Typography>

                      {discount > 0 && (
                        <Chip
                          label={`${discount * 100}% Bulk Discount Applied`}
                          color="success"
                          sx={{ mt: 2 }}
                        />
                      )}

                      <Typography variant="h6" mt={2} color="primary">
                        ₹{total.toLocaleString()} /{" "}
                        {billing === "yearly" ? "year" : "quarter"}
                      </Typography>

                      <Divider sx={{ my: 3 }} />

                      {plan.features.map((feature, i) => (
                        <Box key={i} display="flex" mb={1.2}>
                          <Check sx={{ mr: 1 }} />
                          <Typography variant="body2">
                            {feature}
                          </Typography>
                        </Box>
                      ))}

                      <Button
                        fullWidth
                        variant="contained"
                        sx={{ mt: 3 }}
                        onClick={() => navigate("/contact")}
                      >
                        Request a Demo
                      </Button>
                    </Card>
                  </Grid>
                );
              })}

              {/* CUSTOM PLAN */}
              <Grid item xs={12} sm={6} md={4}>
                <Card
                  sx={{
                    p: 4,
                    borderRadius: 4,
                    height: "100%",
                    border: "2px solid #2e7d32",
                    background: "#f1f8f4",
                  }}
                >
                  <Chip label="Custom Plan" color="success" sx={{ mb: 2 }} />

                  <Typography variant="h5" fontWeight={700}>
                    {customPlan.title}
                  </Typography>

                  <Typography variant="h6" mt={2}>
                    Contact for Pricing
                  </Typography>

                  <Divider sx={{ my: 3 }} />

                  {customPlan.features.map((feature, i) => (
                    <Box key={i} display="flex" mb={1.2}>
                      <Check sx={{ mr: 1 }} />
                      <Typography variant="body2">
                        {feature}
                      </Typography>
                    </Box>
                  ))}

                  <Button
                    fullWidth
                    variant="contained"
                    color="success"
                    sx={{ mt: 3 }}
                    onClick={() => navigate("/contact")}
                  >
                    Contact Us
                  </Button>
                </Card>
              </Grid>
            </Grid>
          </>
        )
      }

      <ParentLoginDialog
        open={openLogin}
        handleClose={() => setOpenLogin(false)}
      />
    </Container >
  );
};

export default PricingPlan;